import { FC, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import {
  MiAlert,
  MiButton,
  MiButtonGroup,
  MiMenu,
  MiModal,
  MiModalConfirm,
  MiSwitch,
} from "@miplatform/components";
import { ICONS_WEB, MODAL_TITLES } from "@miplatform/constants";
import { useBoolean } from "@miplatform/hooks";
import {
  AutoTriggerOffsetProperty,
  ButtonSizes,
  ButtonVariants,
  Colors,
  IApiRequest,
  IAutoTriggerFlow,
  IOrderTemplateResponse,
  IWorkOrderDependencyMaps,
} from "@miplatform/types";
import { mdiAlertOutline, mdiPlus, mdiTrashCanOutline } from "@mdi/js";
import { Box, Typography, useTheme } from "@mui/material";
import { Footer } from "../../../../../components/Footer/Footer";
import { NavBlockerModal } from "../../../../../components/NavBlockerModal/NavBlockerModal";
import { ConfigAccordionHeader } from "../../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import {
  AUTO_TRIGGER_DISPLAY_MODE_OPTIONS,
  AutoTriggerView,
  DEFAULT_ORDER_AUTO_TRIGGER_MAP_TYPES,
} from "../autoTrigger.constants";
import {
  AutoTriggerDisplayModeOption,
  IExtendedWorkOrderDependencyMaps,
  SortedDependencyMap,
} from "../autoTrigger.types";
import {
  buildAutoTriggerFlows,
  deriveLabels,
  recomputeDependencyLabels,
} from "../autoTrigger.utils";
import { AddAutoTriggerOrderTemplateModal } from "./AddAutoTriggerOrderTemplateModal";
import { ChartView } from "./ChartView/ChartView";
import { DependencyCard, IDependencyCardLabel } from "./DependencyCard";
import { Separator, getSeparatorConfig } from "./Separator";

export interface AutoTriggerFlowFormValues {
  id: string;
  dependencyMaps: Partial<IWorkOrderDependencyMaps>[];
  modifiedOn: string;
}

export interface AutoTriggerFlowProps {
  flow: IAutoTriggerFlow;
  activeView: AutoTriggerView;
  orderTemplates: IOrderTemplateResponse[];
  templatesToFlowMap: Record<string, string>;
  disabledBuilders: Record<string, boolean>;
  buildersMap: Record<string, string>;
  builderId?: string;
  isLoading?: boolean;
  onViewChange: (view: AutoTriggerView) => void;
  onSubmit?: (
    data: AutoTriggerFlowFormValues,
    onSuccess?: (flow: IAutoTriggerFlow) => void
  ) => void;
  onDelete: (params: IApiRequest) => void;
  onBuilderUpdate: (
    params: IApiRequest & { builderId: string; isEnabled: boolean }
  ) => void;
}

const sortItemsByDependencyHierarchy = (
  items: IExtendedWorkOrderDependencyMaps[]
): IExtendedWorkOrderDependencyMaps[] => {
  const itemMap = new Map<string, IExtendedWorkOrderDependencyMaps>();
  const childrenMap = new Map<string, IExtendedWorkOrderDependencyMaps[]>();
  const rootItems: IExtendedWorkOrderDependencyMaps[] = [];

  items.forEach((item) => itemMap.set(item.id, item));

  items.forEach((item) => {
    const parentId = item.principalId;
    if (parentId && parentId !== item.id && itemMap.has(parentId)) {
      const children = childrenMap.get(parentId) || [];
      children.push(item);
      childrenMap.set(parentId, children);
    } else {
      rootItems.push(item);
    }
  });

  const result: IExtendedWorkOrderDependencyMaps[] = [];
  const traverse = (node: IExtendedWorkOrderDependencyMaps) => {
    result.push(node);
    const children = childrenMap.get(node.id) || [];
    children.forEach((child) => traverse(child));
  };

  rootItems.forEach((root) => traverse(root));
  return result;
};

export const filterDependencyMapsByBuilder = (
  maps: Partial<IWorkOrderDependencyMaps>[],
  targetBuilderId?: string
): Partial<IWorkOrderDependencyMaps>[] => {
  if (!targetBuilderId) {
    return maps.filter((m) => !m.builderId);
  }

  const mapPairKeyToSelected = new Map<
    string,
    Partial<IWorkOrderDependencyMaps>
  >();

  maps.forEach((m) => {
    if (m.builderId && m.builderId !== targetBuilderId) {
      return;
    }

    const key = `${m.dependentId ?? ""}_${m.principalId ?? ""}`;
    const existing = mapPairKeyToSelected.get(key);

    if (!existing || m.builderId === targetBuilderId) {
      mapPairKeyToSelected.set(key, m);
    }
  });

  return Array.from(mapPairKeyToSelected.values());
};

export const AutoTriggerFlow: FC<AutoTriggerFlowProps> = ({
  flow,
  activeView,
  orderTemplates,
  templatesToFlowMap,
  builderId,
  isLoading = false,
  buildersMap,
  disabledBuilders,
  onViewChange,
  onSubmit,
  onDelete,
  onBuilderUpdate,
}) => {
  const theme = useTheme();
  const [
    isAddOrdersModalOpen,
    handleAddOrdersModalOpen,
    handleAddOrdersModalClose,
  ] = useBoolean();
  const [
    isDeleteFlowModalOpen,
    handleDeleteFlowModalOpen,
    handleDeleteFlowModalClose,
  ] = useBoolean();
  const [isConfirmModalOpen, handleConfirmModalOpen, handleConfirmModalClose] =
    useBoolean();

  const [linkingSourceIndex, setLinkingSourceIndex] = useState<number | null>(
    null
  );
  const [initialLinkingState, setInitialLinkingState] = useState<
    Partial<IWorkOrderDependencyMaps>[] | null
  >(null);
  const [invalidCardForms, setInvalidCardForms] = useState<
    Record<string, boolean>
  >({});
  const hasInvalidForms = Object.values(invalidCardForms).some(Boolean);
  const isSaveDisabled =
    hasInvalidForms || isLoading || linkingSourceIndex !== null;
  const headerTitle = `${flow.name}${
    activeView === AutoTriggerView.Chart ? " Chart" : ""
  }`;
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    getValues,
    formState: { isDirty },
  } = useForm<AutoTriggerFlowFormValues>({
    defaultValues: {
      dependencyMaps: flow.dependencyMaps || [],
    },
  });

  const { fields } = useFieldArray({
    control,
    name: "dependencyMaps",
  });
  const dependencyMaps = watch("dependencyMaps") || [];
  const visibleDependencyMaps = useMemo(
    () => filterDependencyMapsByBuilder(dependencyMaps, builderId),
    [dependencyMaps, builderId]
  );
  const selectedTemplates = useMemo(
    () =>
      new Set<string>(
        dependencyMaps
          .flatMap(({ dependentId, principalId }) => [dependentId, principalId])
          .filter((id): id is string => typeof id === "string")
      ),
    [dependencyMaps]
  );
  const extendedDependencyItems = useMemo(() => {
    return visibleDependencyMaps.map((item, index) => {
      const id =
        item.dependentId ||
        item.principalId ||
        fields[index]?.id ||
        `item-${index}`;

      const offset = item.offsetToPrincipal ?? 0;
      const days =
        offset > 0 ? Math.floor(offset / 24) : Math.ceil(offset / 24);

      const matchingTemplate = orderTemplates.find((t) => t.id === id);

      return {
        ...item,
        id,
        dependentId: item.dependentId || id,
        principalId: item.principalId || null,
        dependent: { name: matchingTemplate?.name || `Order (${id})` },
        dependencyLabel: item.dependencyLabel || null,
        dependentOn: item.principalId ? [item.principalId] : [],
        days,
        hours: offset % 24,
        earliestScheduleDaysFromDueDate:
          item.earliestScheduleDaysFromDueDate ?? 0,
        offsetTargetProperty: item.offsetTargetProperty,
      } as IExtendedWorkOrderDependencyMaps;
    });
  }, [visibleDependencyMaps, fields, orderTemplates]);

  const sortedDependencyMap = useMemo(() => {
    const result: SortedDependencyMap = {};
    extendedDependencyItems.forEach((item) => {
      (result[item.days] ||= []).push(item);
    });

    if (!result[0]) {
      result[0] = [];
    }

    Object.keys(result).forEach((dayKey) => {
      const numericKey = Number(dayKey);
      result[numericKey] = sortItemsByDependencyHierarchy(result[numericKey]);
    });

    return result;
  }, [extendedDependencyItems]);

  const derivedLabels = useMemo(
    () => deriveLabels(sortedDependencyMap),
    [sortedDependencyMap]
  );

  const sortedDayKeys = useMemo(() => {
    return Object.keys(sortedDependencyMap)
      .map(Number)
      .sort((a, b) => a - b);
  }, [sortedDependencyMap]);

  const doesCustomFlowExists = useMemo(
    () =>
      dependencyMaps.some(
        (map) => !!map.builderId && !disabledBuilders[map.builderId]
      ),
    [dependencyMaps, disabledBuilders]
  );

  const updateDependencyMapField = (
    index: number,
    field: keyof IWorkOrderDependencyMaps,
    value: number | string
  ) => {
    const currentMaps = getValues("dependencyMaps");
    const updatedMaps = [...currentMaps];
    const targetMap = visibleDependencyMaps[index];

    if (!targetMap) return;

    const targetIndex = currentMaps.findIndex((m) => {
      const isSamePair =
        m.dependentId === targetMap.dependentId &&
        m.principalId === targetMap.principalId;
      return builderId
        ? isSamePair && m.builderId === builderId
        : isSamePair && !m.builderId;
    });

    if (builderId && targetMap.builderId !== builderId) {
      const existingBuilderMapIndex = updatedMaps.findIndex(
        (m) =>
          m.dependentId === targetMap.dependentId &&
          m.principalId === targetMap.principalId &&
          m.builderId === builderId
      );

      if (existingBuilderMapIndex !== -1) {
        updatedMaps[existingBuilderMapIndex] = {
          ...updatedMaps[existingBuilderMapIndex],
          ...(field === "offsetTargetProperty" &&
          value === AutoTriggerOffsetProperty.CompleteDate
            ? {
                [field]: value,
                offsetToPrincipal: 0,
              }
            : { [field]: value }),
        };
      } else {
        const newBuilderOverride: Partial<IWorkOrderDependencyMaps> = {
          ...targetMap,
          id: undefined,
          builderId,
          ...(field === "offsetTargetProperty" &&
          value === AutoTriggerOffsetProperty.CompleteDate
            ? {
                [field]: value,
                offsetToPrincipal: 0,
              }
            : { [field]: value }),
        };

        updatedMaps.push(newBuilderOverride);
      }
    } else {
      const indexToUpdate = targetIndex !== -1 ? targetIndex : index;

      if (updatedMaps[indexToUpdate]) {
        updatedMaps[indexToUpdate] = {
          ...updatedMaps[indexToUpdate],
          ...(field === "offsetTargetProperty" &&
          value === AutoTriggerOffsetProperty.CompleteDate
            ? {
                [field]: value,
                offsetToPrincipal: 0,
              }
            : { [field]: value }),
        };
      }
    }

    setValue("dependencyMaps", updatedMaps, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  const handleAddTemplatesSubmit = (selectedTemplateIds: string[]) => {
    const selectedSet = new Set(selectedTemplateIds);
    const currentMaps = getValues("dependencyMaps");

    const updatedMaps = currentMaps
      .filter((map) => map.dependentId && selectedSet.has(map.dependentId))
      .map((map) => {
        if (map.principalId && !selectedSet.has(map.principalId)) {
          return {
            ...map,
            principalId: undefined,
            dependencyLabel: null,
          };
        }
        return map;
      });

    const existingIds = new Set(updatedMaps.map((map) => map.dependentId));
    selectedTemplateIds.forEach((id) => {
      if (!existingIds.has(id)) {
        updatedMaps.push({
          ...DEFAULT_ORDER_AUTO_TRIGGER_MAP_TYPES,
          dependentId: id,
          offsetToPrincipal: 0,
          earliestScheduleDaysFromDueDate: 0,
          offsetTargetProperty:
            AutoTriggerOffsetProperty.EffectiveScheduledDate,
          dependencyLabel: null,
        });
      }
    });

    const finalMaps = recomputeDependencyLabels(updatedMaps);

    setValue("dependencyMaps", finalMaps, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });

    handleAddOrdersModalClose();
  };

  const handleStartLinkEdit = (index: number) => {
    setInitialLinkingState(getValues("dependencyMaps"));
    setLinkingSourceIndex(index);
  };

  const handleCancelLinkEdit = () => {
    if (initialLinkingState) {
      setValue("dependencyMaps", initialLinkingState, { shouldDirty: true });
    }

    setLinkingSourceIndex(null);
    setInitialLinkingState(null);
  };

  const handleConfirmLinkEdit = () => {
    setLinkingSourceIndex(null);
    setInitialLinkingState(null);
  };

  const handleCheckDependency = (
    sourceIndex: number,
    targetItemId: string,
    isCurrentlyChecked: boolean
  ) => {
    const currentMaps = [...getValues("dependencyMaps")];
    const currentItem = visibleDependencyMaps[sourceIndex];
    const allItemsWithSameIds = currentMaps.filter(
      ({ dependentId, principalId }) =>
        dependentId === currentItem.dependentId &&
        principalId === currentItem.principalId
    );

    allItemsWithSameIds.forEach((item) => {
      const index = currentMaps.findIndex(
        (m) =>
          m.dependentId === item.dependentId &&
          m.principalId === item.principalId
      );

      if (isCurrentlyChecked) {
        currentMaps[index] = {
          ...currentMaps[index],
          principalId: undefined,
          dependencyLabel: null,
        };
      } else {
        currentMaps[index] = {
          ...currentMaps[index],
          principalId: targetItemId,
        };
      }
    });

    const finalMaps = recomputeDependencyLabels(currentMaps);

    setValue("dependencyMaps", finalMaps, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleOverrideFlowSave = (data: AutoTriggerFlowFormValues) => {
    onSubmit?.(
      {
        ...data,
        dependencyMaps: data.dependencyMaps.filter(
          ({ builderId }) => !builderId
        ),
        id: flow.id,
        modifiedOn: flow.modifiedOn,
      },
      (flow: IAutoTriggerFlow) => {
        reset({ dependencyMaps: buildAutoTriggerFlows(flow.dependencyMaps) });
      }
    );
  };

  const handleSaveForm = (data: AutoTriggerFlowFormValues) => {
    onSubmit?.(
      {
        ...data,
        id: flow.id,
        modifiedOn: flow.modifiedOn,
      },
      (flow: IAutoTriggerFlow) => {
        reset({ dependencyMaps: buildAutoTriggerFlows(flow.dependencyMaps) });
      }
    );
  };

  const handleDelete = () =>
    onDelete({ id: flow.id, modifiedOn: flow.modifiedOn });

  const handleOffsetFormValidityChange = useCallback(
    (id: string, isValid: boolean) => {
      setInvalidCardForms((prev) => ({
        ...prev,
        [id]: !isValid,
      }));
    },
    []
  );

  const handleReset = () => {
    const rawMaps = flow.dependencyMaps || [];
    const normalizedMaps = recomputeDependencyLabels(rawMaps);

    reset({
      dependencyMaps: normalizedMaps,
    });
  };

  const renderContent = () => {
    if (fields.length === 0) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={5}
          minHeight={169}
        >
          <Typography
            fontWeight={theme.fontWeight.semiBold}
            fontSize={theme.fontSize.xlarge}
            align="center"
            gutterBottom
          >
            No Orders Added
          </Typography>
          <Typography
            variant="body2"
            align="center"
            color={theme.palette.grey.medium}
          >
            Add Order Types to begin setting up order Auto-Trigger.
          </Typography>
        </Box>
      );
    }

    if (disabledBuilders[builderId ?? ""]) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          p={2}
          minHeight={78}
        >
          <Typography
            fontWeight={theme.fontWeight.semiBold}
            fontSize={theme.fontSize.xlarge}
            align="center"
            gutterBottom
          >
            {`${flow.name || ""} Disabled`}
          </Typography>
        </Box>
      );
    }

    if (activeView === AutoTriggerView.Chart) {
      return (
        <Box p={2} height={500}>
          <ChartView
            isNumbered
            sortedDependencyMap={sortedDependencyMap}
            labels={derivedLabels}
            orderTemplateName={flow.name ?? null}
          />
        </Box>
      );
    }

    return (
      <Box display="flex" flexDirection="column" gap={3} p={2}>
        {sortedDayKeys.map((dayKey) => {
          const rawGroupItems = sortedDependencyMap[dayKey] || [];
          const sortedGroupItems =
            sortItemsByDependencyHierarchy(rawGroupItems);
          const { title, type } = getSeparatorConfig(dayKey, true);

          return (
            <Box
              key={`group-${dayKey}`}
              display="flex"
              flexDirection="column"
              gap={2}
            >
              <Separator title={title} type={type} />

              {sortedGroupItems.map((groupItem) => {
                let index = -1;

                if (builderId) {
                  index = visibleDependencyMaps.findIndex(
                    (m) =>
                      (m.dependentId === groupItem.id ||
                        m.dependentId === groupItem.dependentId) &&
                      m.builderId === builderId
                  );
                }

                if (index === -1) {
                  index = visibleDependencyMaps.findIndex(
                    (m) =>
                      (m.dependentId === groupItem.id ||
                        m.dependentId === groupItem.dependentId) &&
                      (!m.builderId || !builderId)
                  );
                }

                if (index === -1) return null;
                const currentItem = visibleDependencyMaps[index];
                const itemId = groupItem.id;

                const matchingTemplate = orderTemplates.find(
                  (t) => t.id === itemId
                );
                const cardTitle = matchingTemplate?.name || "Order";

                const totalHours = currentItem.offsetToPrincipal ?? 0;
                const days = Math.floor(totalHours / 24).toString();
                const hours = (totalHours % 24).toString();

                const isLinkingThisCard = linkingSourceIndex === index;
                const isLinkingActive = linkingSourceIndex !== null;

                const linkingSourceItem =
                  linkingSourceIndex !== null
                    ? visibleDependencyMaps[linkingSourceIndex]
                    : null;
                const linkingSourceId =
                  linkingSourceItem?.dependentId ||
                  linkingSourceItem?.principalId;

                const isChecked =
                  isLinkingActive && linkingSourceItem?.principalId === itemId;

                const isCycle =
                  isLinkingActive &&
                  currentItem.principalId === linkingSourceId;

                const isDisabled =
                  isLoading ||
                  (isLinkingActive && (itemId === linkingSourceId || isCycle));

                const indentation = currentItem.dependencyLabel
                  ? Number(currentItem.dependencyLabel)
                  : 0;

                const selfLabels: IDependencyCardLabel[] = derivedLabels
                  .filter(
                    (l) => l.displayOnMapId === itemId && l.mapId === itemId
                  )
                  .map((l) => ({
                    dependencyLabel: l.dependencyLabel?.replace(/\.$/, ""),
                    color: l.color,
                    labelColor: l.labelColor,
                  }));

                const dependencyLabel = derivedLabels.find(
                  (l) => l.displayOnMapId === groupItem.principalId
                );
                const dependencyLabels: IDependencyCardLabel[] = dependencyLabel
                  ? [
                      {
                        ...dependencyLabel,
                        dependencyLabel: `${dependencyLabel?.dependencyLabel} ${
                          orderTemplates.find(
                            (o) => o.id === groupItem.principalId
                          )?.name || ""
                        }`,
                      },
                    ]
                  : [];

                return (
                  <Fragment key={itemId}>
                    <DependencyCard
                      itemId={itemId}
                      title={cardTitle}
                      indentation={indentation}
                      isDisabled={isDisabled}
                      areFieldsDisabled={!currentItem.principalId}
                      isAddingDependency={isLinkingThisCard}
                      canBeSelected={isLinkingActive && !isLinkingThisCard}
                      isLinkingDisabled={!!builderId}
                      isChecked={isChecked}
                      days={days}
                      hours={hours}
                      earliestScheduleDaysFromDueDate={String(
                        currentItem.earliestScheduleDaysFromDueDate ?? 0
                      )}
                      offsetTargetProperty={currentItem.offsetTargetProperty}
                      labels={selfLabels}
                      dependencyLabels={dependencyLabels}
                      onCheck={() => {
                        if (linkingSourceIndex !== null) {
                          handleCheckDependency(
                            linkingSourceIndex,
                            itemId,
                            isChecked
                          );
                        }
                      }}
                      onOffsetFormValidityChange={
                        handleOffsetFormValidityChange
                      }
                      setAddingDependency={() => handleStartLinkEdit(index)}
                      onCancel={handleCancelLinkEdit}
                      onConfirm={handleConfirmLinkEdit}
                      editDays={(newDays) => {
                        const currentTotal = currentItem.offsetToPrincipal ?? 0;
                        const currentHours = currentTotal % 24;
                        const newTotal = newDays * 24 + currentHours;
                        updateDependencyMapField(
                          index,
                          "offsetToPrincipal",
                          newTotal
                        );
                      }}
                      editHours={(newHours) => {
                        const currentTotal = currentItem.offsetToPrincipal ?? 0;
                        const currentDays =
                          currentTotal > 0
                            ? Math.floor(currentTotal / 24)
                            : Math.ceil(currentTotal / 24);
                        const newTotal = currentDays * 24 + newHours;
                        updateDependencyMapField(
                          index,
                          "offsetToPrincipal",
                          newTotal
                        );
                      }}
                      editEarliestScheduleDaysFromDueDate={(val) => {
                        updateDependencyMapField(
                          index,
                          "earliestScheduleDaysFromDueDate",
                          val
                        );
                      }}
                      editOffsetTargetProperty={(val) => {
                        updateDependencyMapField(
                          index,
                          "offsetTargetProperty",
                          val
                        );
                      }}
                    />

                    {isLinkingThisCard && (
                      <MiAlert
                        isOpen
                        variant="info"
                        description={
                          <>
                            Select the order that <b>{cardTitle}</b> is
                            dependent on.
                          </>
                        }
                        leftIcon={
                          ICONS_WEB.INFORMATION_OUTLINE as unknown as string
                        }
                        iconColor={theme.palette.primary.main}
                        sx={{
                          py: theme.spacing(0.5),
                          px: theme.spacing(3),
                          fontSize: theme.fontSize.small,
                          fontWeight: theme.fontWeight.medium,
                        }}
                        hideCloseButton
                      />
                    )}
                  </Fragment>
                );
              })}
            </Box>
          );
        })}
      </Box>
    );
  };

  useEffect(() => {
    handleReset();
  }, [flow.dependencyMaps, flow.id, flow.modifiedOn, builderId, reset]);

  return (
    <>
      <ConfigAccordionHeader
        disableCollapse
        title={headerTitle}
        customAction={
          <Box display="flex" gap={2}>
            {builderId ? (
              <MiSwitch
                label="Disable for Customer"
                value={disabledBuilders[builderId]}
                sx={{
                  color: theme.palette.common.white,
                }}
                disabled={isLoading}
                handleChange={(_e, checked: boolean) =>
                  onBuilderUpdate({
                    id: flow.id,
                    builderId,
                    isEnabled: !checked,
                    modifiedOn: flow.modifiedOn,
                  })
                }
              />
            ) : (
              <>
                <MiButtonGroup
                  containerHeight={24}
                  selected={activeView}
                  data={[...AUTO_TRIGGER_DISPLAY_MODE_OPTIONS]}
                  handleClick={(item: AutoTriggerDisplayModeOption) =>
                    onViewChange(item.value)
                  }
                />
                <MiButton
                  color={Colors.White}
                  variant={ButtonVariants.Text}
                  size={ButtonSizes.Small}
                  disabled={isLoading || !!builderId}
                  sx={{
                    border: `1px solid ${theme.palette.common.white}`,
                    height: 24,
                    ...((isLoading || !!builderId) && {
                      opacity: 0.5,
                    }),
                  }}
                  startIcon={{
                    path: mdiPlus,
                    iconColor: theme.palette.common.white,
                  }}
                  title={dependencyMaps.length > 0 ? "Edit Flow" : "Add Orders"}
                  onClick={handleAddOrdersModalOpen}
                />
                <MiMenu
                  iconColor={theme.palette.common.white}
                  data={[
                    {
                      label: "Delete",
                      icon: mdiTrashCanOutline,
                      color: theme.palette.error.main,
                      disabled: isLoading,
                      "data-testid": "delete-flow-button",
                      action: handleDeleteFlowModalOpen,
                    },
                  ]}
                />
              </>
            )}
          </Box>
        }
      >
        {renderContent()}
      </ConfigAccordionHeader>

      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1,
        }}
      >
        <Footer
          show={isDirty}
          saveTitle="Save Changes"
          disableSave={
            hasInvalidForms || isLoading || linkingSourceIndex !== null
          }
          isDirty={isDirty}
          handleSaveClick={
            doesCustomFlowExists && !builderId
              ? handleConfirmModalOpen
              : handleSubmit(handleSaveForm)
          }
          handleCancelClick={handleReset}
        />
      </Box>
      <AddAutoTriggerOrderTemplateModal
        isOpen={isAddOrdersModalOpen}
        orderTemplates={orderTemplates}
        templatesToFlowMap={templatesToFlowMap}
        onClose={handleAddOrdersModalClose}
        onSubmit={handleAddTemplatesSubmit}
        selectedTemplates={selectedTemplates}
      />
      <NavBlockerModal
        isDirty={isDirty}
        isSaveDisabled={isSaveDisabled}
        onSave={handleSubmit(handleSaveForm)}
      />
      <MiModal
        open={isConfirmModalOpen}
        title="Builder-Specific Flows Will Be Affected"
        titleIcon={mdiAlertOutline}
        leftIconColor={theme.palette.warning.main}
        closeModal={handleConfirmModalClose}
        width={470}
        description="The builders below have custom flows. Are you sure you want to overwrite their custom flow with the updated default flow?"
        actions={[
          {
            name: "Update Default Flow Only",
            variant: ButtonVariants.Outlined,
            color: Colors.Grey,
            disabled: isLoading,
            onClick: () => {
              handleSubmit(handleSaveForm)();
              handleConfirmModalClose();
            },
          },
          {
            name: "Update Builder-Specific Flows",
            variant: ButtonVariants.Contained,
            color: Colors.Warning,
            disabled: isLoading,
            onClick: () => {
              handleSubmit(handleOverrideFlowSave)();
              handleConfirmModalClose();
            },
          },
        ]}
      >
        <Box pl={5} pr={3} py={2}>
          {Array.from(
            new Set(
              dependencyMaps
                .filter(({ builderId }) => builderId)
                .map(({ builderId }) => builderId as string)
            )
          )
            .sort((a: string, b: string) =>
              buildersMap[a].localeCompare(buildersMap[b])
            )
            .map((builderId) => (
              <Typography
                key={builderId}
                component="li"
                color={theme.palette.grey.medium}
                fontSize={theme.fontSize.medium}
              >
                {buildersMap[builderId]}
              </Typography>
            ))}
        </Box>
      </MiModal>
      <MiModalConfirm
        open={isDeleteFlowModalOpen}
        textPositive="Delete"
        title={MODAL_TITLES.DELETE}
        titleIcon={{
          path: mdiTrashCanOutline,
          color: theme.palette.error.main,
        }}
        buttonPositiveColor={Colors.Error}
        handleNegative={handleDeleteFlowModalClose}
        handlePositive={() => {
          handleDelete();
          handleDeleteFlowModalClose();
        }}
        description={`Are you sure you want to delete ${flow.name}?`}
      />
    </>
  );
};
